<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { formatDayMonthDate, formatShortDate } from '@/lib/utils'
import { useChips } from '@/composables/chips'
import { signalToneEnum, signalToneColor } from '@/composables/dashboard-kpi'
import { useReportFormatter } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePlanningStore } from '@/stores/planning.store'

import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  row: {
    type: Object,
    required: true,
  },
  sectionKey: {
    type: String,
    required: true,
  },
  build: {
    type: Object,
    required: true,
  },
  subject: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['click'])

const i18n = getI18n()
const accountingStore = useAccountingStore()
const planningStore = usePlanningStore()
const { getInvoiceStatusChips } = useChips()
const formatter = useReportFormatter()
const { reportSectionKeyEnum, expenseSectionKeys, reportSectionIcons } = accountingStore

const DAY_SECTIONS = Object.freeze([reportSectionKeyEnum.DAILY, reportSectionKeyEnum.STAFF_BREAKDOWN, reportSectionKeyEnum.HOURS])
const PROJECT_SECTIONS = Object.freeze([reportSectionKeyEnum.PROJECTS, reportSectionKeyEnum.PROJECTS_CLIENTS])

const isDay = computed(() => DAY_SECTIONS.includes(props.sectionKey))
const isAggregateDay = computed(() => props.sectionKey === reportSectionKeyEnum.HOURS
  || (props.sectionKey === reportSectionKeyEnum.DAILY && !accountingStore.isStaffSubject(props.subject)))
const duration = (minutes) => formatter.duration(minutes, props.build)
const money = (value, currency) => formatter.money(value, currency || props.build.reference_currency)
const attentionColor = signalToneColor(signalToneEnum.ATTENTION)

const dayMarks = computed(() => [
  { key: 'people', icon: 'mdi-account-multiple-outline', text: isAggregateDay.value && props.row.people > 1 ? String(props.row.people) : '' },
  { key: 'travel', icon: 'mdi-car-outline', text: props.row.travel ? duration(props.row.travel) : '' },
  { key: 'night', icon: 'mdi-weather-night', text: props.row.night ? duration(props.row.night) : '' },
].filter(mark => mark.text))

const content = computed(() => {
  const { row } = props
  if (isDay.value) {
    return {
      label: props.sectionKey === reportSectionKeyEnum.STAFF_BREAKDOWN ? row.staff : row.project,
      caption: [
        row.start ? `${formatter.time(row.start, row)}–${formatter.time(row.end, row, true)}` : '',
        props.sectionKey === reportSectionKeyEnum.STAFF_BREAKDOWN ? row.project : row.comments,
      ].filter(Boolean).join(' · '),
      rightLabel: duration(row.net),
    }
  }
  if (expenseSectionKeys.includes(props.sectionKey)) {
    return {
      avatarIcon: reportSectionIcons[props.sectionKey],
      label: row.notes || row.tag,
      caption: [row.date ? formatDayMonthDate(row.date) : '', row.staff && accountingStore.isStaffSubject(props.subject) ? '' : row.staff, row.project, row.tag].filter(Boolean).join(' · '),
      rightLabel: money(row.amount, row.currency),
      rightCaption: row.unit_cost != null ? `${formatter.number(row.quantity)} × ${money(row.unit_cost, row.currency)}` : '',
    }
  }
  if (PROJECT_SECTIONS.includes(props.sectionKey)) {
    const isClientList = props.sectionKey === reportSectionKeyEnum.PROJECTS_CLIENTS
    const status = planningStore.getLocalizedProjectStatuses().find(item => item.id === row.status_value)
    return {
      avatarIcon: reportSectionIcons[props.sectionKey],
      label: row.project,
      caption: isClientList
        ? [row.client, row.days != null ? i18n.t('common.report.daysCount', row.days) : ''].filter(Boolean).join(' · ')
        : [row.project_start, row.project_end].filter(Boolean).map(value => formatShortDate(value)).join(' – '),
      captionChips: status ? [{ ...status, dense: true }] : null,
      rightLabel: duration(row.net),
      rightCaption: row.expenses_reference ? money(row.expenses_reference) : '',
    }
  }
  if (props.sectionKey === reportSectionKeyEnum.QUOTES) {
    return {
      avatarIcon: reportSectionIcons[props.sectionKey],
      label: row.name,
      caption: [row.number ? i18n.t('common.report.documentNumber', { number: row.number }) : '', row.document_date ? formatShortDate(row.document_date) : ''].filter(Boolean).join(' · '),
      rightLabel: money(row.total, row.currency),
    }
  }
  return {
    avatarIcon: reportSectionIcons[props.sectionKey],
    label: row.number,
    caption: [row.document_date ? formatShortDate(row.document_date) : '', row.projects].filter(Boolean).join(' · '),
    captionChips: getInvoiceStatusChips(row.status_value).map(item => ({ ...item, dense: true })),
    rightLabel: money(row.total, row.currency),
  }
})
</script>

<template>
  <list-item
    class="q-px-sm q-py-xs"
    :avatar-icon="content.avatarIcon"
    avatar-icon-color="primary"
    :label="content.label"
    :caption-chips="content.captionChips?.length ? content.captionChips : null"
    :right-label="content.rightLabel"
    :right-caption="content.rightCaption"
    dense
    clickable
    @click="emit('click', props.row)"
  >
    <template v-if="isDay" #avatar>
      <div class="report-day text-center">
        <div class="text-subtitle1 text-weight-medium">{{ dayjs(props.row.date).format('DD') }}</div>
        <div class="text-caption text-grey-7 text-uppercase">{{ dayjs(props.row.date).format('ddd') }}</div>
      </div>
    </template>
    <template v-if="content.caption || isDay" #caption>
      <div class="row items-center no-wrap q-gutter-x-xs">
        <q-icon
          v-if="isDay && props.row.assumed"
          name="mdi-circle"
          size="0.5rem"
          :color="attentionColor"
        >
          <q-tooltip>{{ i18n.t('common.report.assumedTime') }}</q-tooltip>
        </q-icon>
        <span class="ellipsis" style="min-width: 0;">{{ content.caption }}</span>
        <span v-for="mark in dayMarks" :key="mark.key" class="row items-center no-wrap">
          <q-icon :name="mark.icon" size="0.875rem" />{{ mark.text }}
        </span>
        <q-icon v-if="!isDay && props.row.attachments" name="mdi-paperclip" size="0.875rem" />
      </div>
    </template>
  </list-item>
</template>

<style scoped lang="scss">
.report-day {
  width: 2.5rem;
  line-height: 1.1;
}
</style>
